// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspFormEdit.tsx
import React, { useState, useMemo, useEffect, Key } from 'react';
import { Form, Input, Spin, Row, Col, message, Card, Transfer, Select, Result } from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetQaqcInspFormData } from '../../../hooks/genericHooks'; // Hook for fetching all QA/QC checklist items
import { fetchSqlData, updateData } from '../../../services/api'; // Fetch for existing data, updateData for saving
import type { QaqcInspFormDesigns, VwQaqcInspFormDetails, QaqcInspFormData } from '../../../types';
import Button from '../../../components/common/Button';

// Define the structure for items used in the Transfer component
interface TransferItem {
  key: string;
  title: string;
  description: string;
  disabled: boolean;
  category: string;
  check_item: string;
  discipline: string;
  // inspform_number might not be needed here unless used for display/filtering
}

const { Option } = Select;

const QaqcInspFormEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const designId = id ? parseInt(id, 10) : undefined;

  // State for Transfer component and filters
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
  const [selectedDisciplineFilter, setSelectedDisciplineFilter] = useState<string | null>(null);
  const [searchCheckItemText, setSearchCheckItemText] = useState<string>('');

  // --- Data Fetching ---
  // 1. Fetch ALL possible QA/QC checklist items for the Transfer dataSource
  const {
    data: checklistDataResult,
    isLoading: isLoadingItems,
    error: errorLoadingItems,
  } = useGetQaqcInspFormData();

  // 2. Fetch the SPECIFIC QA/QC form design details to edit
  const {
    data: existingFormDetails, // Array of VwQaqcInspFormDetails for this design_id
    isLoading: isLoadingExisting,
    error: errorLoadingExisting,
    isFetching: isFetchingExisting,
  } = useQuery<VwQaqcInspFormDetails[], Error>({
    queryKey: ['qaqcFormDetail', designId],
    queryFn: async () => {
      if (!designId) throw new Error("No valid form design ID provided");
      const results = await fetchSqlData('vw_qaqc_insp_formdetails', 'design_id', designId);
      if (!results || !Array.isArray(results)) {
        throw new Error(`Invalid response when fetching form details for ID ${designId}.`);
      }
      if (results.length === 0) {
          throw new Error(`Form design with ID ${designId} not found.`);
      }
      return results;
    },
    enabled: !!designId,
    staleTime: 1000 * 60 * 1, // 1 minute cache
  });

  // --- Data Preparation ---
  const allChecklistItems = useMemo(() => checklistDataResult?.data || [], [checklistDataResult]);
  const categories = useMemo(() => Array.from(new Set(allChecklistItems.map(item => item.category).filter(Boolean))).sort(), [allChecklistItems]);
  const disciplines = useMemo(() => Array.from(new Set(allChecklistItems.map(item => item.discipline).filter(Boolean))).sort(), [allChecklistItems]);

  const transferDataSource = useMemo((): TransferItem[] => {
    const allItemsMapped = allChecklistItems
      .filter(item => item.id != null && item.check_item != null && item.category != null && item.discipline != null)
      .map(item => ({
        key: item.id.toString(),
        title: `${item.check_item} (${item.category} / ${item.discipline})`,
        description: `${item.category} - ${item.discipline}`,
        disabled: false,
        category: item.category,
        check_item: item.check_item,
        discipline: item.discipline,
      }));

    let availableItems = allItemsMapped;
    if (selectedDisciplineFilter) {
      availableItems = availableItems.filter(item => item.discipline === selectedDisciplineFilter);
    }
    if (selectedCategoryFilter) {
      availableItems = availableItems.filter(item => item.category === selectedCategoryFilter);
    }
    if (searchCheckItemText) {
      availableItems = availableItems.filter(item => item.check_item.toLowerCase().includes(searchCheckItemText.toLowerCase()));
    }

    const selectedItemsMap = new Map<Key, TransferItem>();
    const targetKeySet = new Set(targetKeys);
    allItemsMapped.forEach(item => {
        if (targetKeySet.has(item.key)) {
            selectedItemsMap.set(item.key, item);
        }
    });

    const finalDataSourceMap = new Map<Key, TransferItem>(selectedItemsMap);
    availableItems.forEach(item => {
      if (!finalDataSourceMap.has(item.key)) {
        finalDataSourceMap.set(item.key, item);
      }
    });

    return Array.from(finalDataSourceMap.values());
  }, [allChecklistItems, selectedCategoryFilter, selectedDisciplineFilter, searchCheckItemText, targetKeys]);

  // --- Effect to Populate Form and Transfer on Load ---
  useEffect(() => {
    if (existingFormDetails && existingFormDetails.length > 0) {
      const formInfo = existingFormDetails[0]; // Common info
      const currentFieldIds = existingFormDetails.map(detail => detail.field_id.toString());

      form.setFieldsValue({
        form_num: formInfo.form_num,
        form_name: formInfo.form_name,
      });
      setTargetKeys(currentFieldIds);
    }
  }, [existingFormDetails, form]);

  // --- Update Mutation ---
  const updateMutation = useMutation<unknown, Error, Partial<QaqcInspFormDesigns>>({
    mutationFn: (payload) => {
        if (!designId) throw new Error("Missing Design ID for update.");
        return updateData('qaqc_insp_form_designs', designId, payload);
    },
    onSuccess: () => {
      message.success('QA/QC Form design updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['vw_qaqc_insp_formdetails'] });
      queryClient.invalidateQueries({ queryKey: ['qaqcFormDetail', designId] });
      navigate('/qaqc/inspections/form_list');
    },
    onError: (error: any) => {
      console.error('Error updating QA/QC form design:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to update QA/QC form design.';
      message.error(`Error: ${errorMsg}`);
    },
  });

  // --- Form Submission ---
  const onFinish = async (values: any) => {
    if (targetKeys.length === 0) {
        message.error('Please select at least one check item for the form design.');
        return;
    }
    const payload: Partial<QaqcInspFormDesigns> = {
      form_num: values.form_num,
      form_name: values.form_name,
      form_fields: targetKeys.map(key => parseInt(String(key), 10)),
    };
    updateMutation.mutate(payload);
  };

  // --- Transfer Component Handlers ---
  const handleTransferChange = (newTargetKeys: Key[], direction: TransferDirection, moveKeys: Key[]) => {
    setTargetKeys(newTargetKeys);
  };

  // --- Render Logic ---
  const isLoading = isLoadingItems || isLoadingExisting || isFetchingExisting || updateMutation.isPending;

  if (!designId) {
    return <Result status="error" title="Invalid ID" subTitle="No valid form design ID provided for editing." />;
  }

  if (!isLoadingExisting && errorLoadingExisting) {
    return <Result status="error" title="Error Loading Data" subTitle={errorLoadingExisting.message} extra={<Button type="primary" onClick={() => navigate(-1)}>Go Back</Button>} />;
  }

  return (
    <Card title={`Edit QA/QC Inspection Form Design (ID: ${designId})`}>
      <Spin spinning={isLoading} tip={updateMutation.isPending ? "Updating..." : "Loading..."}>
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Form Number" name="form_num" rules={[{ required: true, message: 'Please input the form number!' }]}>
                <Input placeholder="e.g., QAQC-CIV-001" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Form Name" name="form_name" rules={[{ required: true, message: 'Please input the form name!' }]}>
                <Input placeholder="e.g., Concrete Pour Checklist" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Select Check Items" validateStatus={targetKeys.length === 0 ? 'error' : ''} help={targetKeys.length === 0 ? 'Please select at least one check item!' : ''}>
            <Row gutter={8} style={{ marginBottom: '10px' }}>
              <Col xs={24} sm={8}>
                <Select placeholder="Filter by Discipline" allowClear style={{ width: '100%'}} onChange={(value) => setSelectedDisciplineFilter(value)} value={selectedDisciplineFilter} showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {disciplines.map(discipline => (<Option key={discipline} value={discipline}>{discipline}</Option>))}
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select placeholder="Filter by Category" allowClear style={{ width: '100%'}} onChange={(value) => setSelectedCategoryFilter(value)} value={selectedCategoryFilter} showSearch filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {categories.map(category => (<Option key={category} value={category}>{category}</Option>))}
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Input.Search placeholder="Search check items" allowClear onChange={(e) => setSearchCheckItemText(e.target.value)} style={{ width: '100%' }} />
              </Col>
            </Row>
            <Transfer<TransferItem>
              dataSource={transferDataSource}
              showSearch={false} // Using custom search inputs above
              targetKeys={targetKeys}
              onChange={handleTransferChange}
              render={item => item.title}
              listStyle={{ width: '48%', height: 400 }}
              titles={['Available Items', 'Selected Items']}
              rowKey={record => record.key}
            />
          </Form.Item>

          <Form.Item wrapperCol={{ xs: { span: 24 }, sm: { span: 16, offset: 8 } }} style={{ textAlign: 'right' }}>
            <Button action="edit" type="primary" htmlType="submit" loading={updateMutation.isPending}>
              Update Form Design
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate('/qaqc/inspections/form_list')}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default QaqcInspFormEdit;